import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeftRight, Banknote, Receipt, Waves, MapPin, HelpCircle } from "lucide-react";

const FACTS = [
  { icon: ArrowLeftRight, t: "One bridge, one toll each way", d: "The Sir Sidney Poitier Bridge connects Nassau (New Providence) to Paradise Island. The toll is charged per crossing — so a day trip over and back counts as two trips." },
  { icon: Banknote, t: "Toll is $3 per trip", d: "The bridge toll is $3 per crossing for standard vehicles. Keep it in mind if you're commuting between Nassau and Paradise Island daily — it adds up over a week." },
  { icon: Receipt, t: "Easy Drive Toll Pass — skip the hassle", d: "Add the optional Toll Pass to your booking for an $8.99 service fee plus $3 per trip, billed straight to your invoice. No digging for cash at the booth, no keeping receipts — just drive." },
  { icon: Waves, t: "Walking the bridge is free", d: "Only vehicles pay the toll. If you're staying on Paradise Island and just popping over to Nassau for dinner, the pedestrian walkway is free and the views over the harbour are worth it." },
  { icon: MapPin, t: "Know your exits", d: "Coming back into Nassau, the bridge splits toward downtown Bay Street and West Bay Street. Check your destination first — the wrong fork can add 15 minutes in cruise-ship traffic." },
];

const FAQS = [
  { q: "Is the Paradise Island bridge toll charged both ways?", a: "Yes — the $3 toll applies per crossing, each direction, for vehicles." },
  { q: "Do I have to pay the toll in cash?", a: "Not with an Easy Drive rental. With the Toll Pass option your crossings are logged and simply appear on your invoice at $3 per trip plus the one-time $8.99 service fee." },
  { q: "Is there any way to cross for free?", a: "On foot, yes — the pedestrian walkway over the bridge is free. Vehicles always pay the toll." },
  { q: "Does the toll show up on my final invoice?", a: "Yes. Toll trips are itemized on your Easy Drive invoice so everything is transparent — no surprises." },
];

export default function GuideBridgeToll() {
  useSEO({
    title: "Paradise Island Bridge Toll Guide — Nassau Bahamas | Easy Drive",
    description: "Everything about the Paradise Island bridge toll in Nassau, Bahamas: $3 per crossing on the Sir Sidney Poitier Bridge, how the Easy Drive Toll Pass works, and money-saving tips.",
    path: "/guides/paradise-island-bridge-toll",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Paradise Island Bridge Toll Guide — Nassau, Bahamas",
        description: "How the $3 Paradise Island bridge toll works, the Sir Sidney Poitier Bridge, and the Easy Drive Toll Pass option for rental guests.",
        author: { "@type": "Organization", name: "Easy Drive Car Rental", url: "https://easydrivecarrental.com/" },
        publisher: { "@type": "Organization", name: "Easy Drive Car Rental", logo: { "@type": "ImageObject", url: "https://easydrivecarrental.com/logo.png" } },
        mainEntityOfPage: "https://easydrivecarrental.com/guides/paradise-island-bridge-toll",
        datePublished: "2026-09-20",
        dateModified: "2026-09-20",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ],
  });

  return (
    <div data-testid="guide-bridge-toll-page" className="max-w-4xl mx-auto px-6 lg:px-10 pt-36 pb-24">
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Island Guide</p>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight">
        The <span className="italic gold-text">Paradise Island bridge toll</span>, explained
      </h1>
      <p className="mt-6 text-muted-foreground leading-relaxed font-ui max-w-2xl">
        Heading to Atlantis, Cabbage Beach or the marina? You'll cross the Sir Sidney Poitier Bridge — and every vehicle crossing carries a small toll. Here's exactly how it works so nothing on your trip (or your invoice) surprises you.
      </p>

      <div className="mt-12 space-y-5">
        {FACTS.map(({ icon: Icon, t, d }, i) => (
          <div key={i} data-testid={`guide-toll-fact-${i + 1}`} className="bg-card border border-border rounded-2xl p-6 md:p-7 flex gap-5 transition-colors duration-200 hover:border-primary/40">
            <span className="w-11 h-11 rounded-full border border-primary/30 flex items-center justify-center shrink-0">
              <Icon size={18} className="gold-text" />
            </span>
            <div>
              <h2 className="font-display text-xl md:text-2xl tracking-tight">{t}</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed font-ui">{d}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="mt-14 font-display text-3xl tracking-tight flex items-center gap-3">
        <HelpCircle size={22} className="gold-text" /> Common questions
      </h2>
      <div className="mt-6 space-y-4">
        {FAQS.map((f, i) => (
          <div key={i} data-testid={`guide-toll-faq-${i}`} className="bg-card border border-border rounded-2xl p-6">
            <p className="font-display text-lg tracking-tight">{f.q}</p>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed font-ui">{f.a}</p>
          </div>
        ))}
      </div>

      <div className="mt-14 rounded-2xl border border-primary/25 bg-card p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 shadow-[0_8px_30px_rgba(224,36,52,0.06)]">
        <div>
          <h2 className="font-display text-2xl md:text-3xl tracking-tight">Add the Toll Pass when you book</h2>
          <p className="mt-2 text-sm text-muted-foreground font-ui max-w-md">$8.99 one-time service fee + $3 per trip, itemized on your invoice. Available during checkout on any vehicle.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/fleet">
            <Button data-testid="guide-toll-fleet-cta" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-7 h-11">
              Browse the Fleet <ArrowRight size={15} className="ml-2" />
            </Button>
          </Link>
          <Link data-testid="guide-toll-driving-link" to="/guides/driving-in-nassau" className="inline-flex items-center text-sm font-ui font-semibold text-primary hover:underline underline-offset-4 self-center">
            Also read: Driving in Nassau tips →
          </Link>
        </div>
      </div>
    </div>
  );
}
